import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../../services/api";

const REFRESH_INTERVAL = 30; // seconds

function Hardware() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [history, setHistory] = useState({});
  const countdownRef = useRef(null);

  const fetchDevices = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await apiClient.get("/singularity/devices");
      setDevices(data.devices || []);
    } catch (err) {
      console.error("Failed to fetch devices:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setCountdown(REFRESH_INTERVAL);
    }
  }, []);

  const fetchHistory = useCallback(async (deviceName) => {
    try {
      const data = await apiClient.get(`/singularity/history?device=${deviceName}`);
      setHistory((prev) => ({ ...prev, [deviceName]: data.history || [] }));
    } catch (err) {
      console.error("Failed to fetch history:", err);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Auto-refresh countdown
  useEffect(() => {
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchDevices(true);
          return REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [fetchDevices]);

  // Fetch history for all devices once loaded
  useEffect(() => {
    if (devices.length > 0) {
      devices.forEach((d) => fetchHistory(d.name));
    }
  }, [devices, fetchHistory]);

  const allOnline = devices.length > 0 && devices.every((d) => d.status === "online");
  const offlineCount = devices.filter((d) => d.status === "offline").length;

  const formatRelativeTime = (isoString) => {
    if (!isoString) return "Never";
    const diff = (Date.now() - new Date(isoString + "Z").getTime()) / 1000;
    if (diff < 10) return "Just now";
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getLatencyColor = (ms) => {
    if (ms <= 0) return "var(--color-text-muted)";
    if (ms < 100) return "var(--color-success)";
    if (ms < 500) return "var(--color-warning)";
    return "var(--color-danger)";
  };

  const getUptimePercent = (deviceName) => {
    const hist = history[deviceName] || [];
    const device = devices.find((d) => d.name === deviceName);
    // No transitions in 24h — infer from current status
    if (hist.length === 0) {
      return device && device.status === "online" ? 100 : 0;
    }
    // Calculate uptime by duration between transitions
    const now = Date.now();
    const dayAgo = now - 24 * 3600 * 1000;
    // The status BEFORE the first transition is the opposite
    const firstStatus = hist[0].status;
    const statusBefore = firstStatus === "offline" ? "online" : "offline";
    let onlineMs = 0;
    let prevTime = dayAgo;
    let prevStatus = statusBefore;
    for (const entry of hist) {
      const t = new Date(entry.timestamp + "Z").getTime();
      if (t < dayAgo) { prevStatus = entry.status; continue; }
      const segStart = Math.max(prevTime, dayAgo);
      if (prevStatus === "online") onlineMs += t - segStart;
      prevTime = t;
      prevStatus = entry.status;
    }
    // Final segment to now
    if (prevStatus === "online") onlineMs += now - Math.max(prevTime, dayAgo);
    return Math.round((onlineMs / (now - dayAgo)) * 100);
  };

  if (loading) {
    return (
      <div className="hardware-loading">
        <div className="hardware-skeleton">
          <div className="skeleton-banner" />
          <div className="skeleton-cards">
            <div className="skeleton-card" />
            <div className="skeleton-card" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hardware-container">
      {/* Global Status Banner */}
      <div className={`status-banner ${allOnline ? "all-online" : "has-offline"}`}>
        <div className="status-banner-content">
          <div className="status-banner-left">
            <span className={`status-banner-dot ${allOnline ? "online" : "offline"}`} />
            <span className="status-banner-text">
              {allOnline
                ? "All Systems Operational"
                : `${offlineCount} device${offlineCount > 1 ? "s" : ""} offline`}
            </span>
          </div>
          <div className="status-banner-right">
            <button
              className={`refresh-btn ${refreshing ? "spinning" : ""}`}
              onClick={() => fetchDevices(true)}
              title="Refresh now"
            >
              ↻
            </button>
            <div className="countdown-ring" title={`Next check in ${countdown}s`}>
              <svg viewBox="0 0 32 32">
                <circle
                  cx="16" cy="16" r="14"
                  className="countdown-track"
                />
                <circle
                  cx="16" cy="16" r="14"
                  className="countdown-progress"
                  style={{
                    strokeDashoffset: `${88 - (88 * countdown) / REFRESH_INTERVAL}`,
                  }}
                />
              </svg>
              <span className="countdown-text">{countdown}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Device Cards Grid */}
      <div className="device-grid">
        {devices.map((device) => (
          <DeviceCard
            key={device.name}
            device={device}
            history={history[device.name] || []}
            formatRelativeTime={formatRelativeTime}
            getLatencyColor={getLatencyColor}
            uptimePercent={getUptimePercent(device.name)}
          />
        ))}
      </div>

      {/* Timeline Section */}
      {devices.length > 0 && (
        <div className="timeline-section">
          <h3 className="timeline-title">24-Hour Status Timeline</h3>
          {devices.map((device) => (
            <TimelineBar
              key={device.name}
              device={device}
              history={history[device.name] || []}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DeviceCard({ device, history, formatRelativeTime, getLatencyColor, uptimePercent }) {
  const isOnline = device.status === "online";
  const statusClass = isOnline ? "online" : "offline";

  // Fields already shown in primary metrics - excluded from hover details
  const primaryKeys = new Set([
    "name", "deviceName", "displayName", "role", "status",
    "cpu", "mem", "temp", "disk", "lastSeen", "lastChecked",
    "lastMetricsAt", "consecutiveFailures", "error", "uptime",
  ]);

  const extraFields = Object.entries(device)
    .filter(([k, v]) => !primaryKeys.has(k) && v !== undefined && v !== null && v !== "")
    .map(([k, v]) => ({ label: k, value: String(v) }));

  return (
    <div className={`device-card ${statusClass}`}>
      <div className="device-card-header">
        <div className="device-identity">
          <span className={`device-status-dot ${statusClass}`} />
          <div className="device-name-group">
            <span className="device-name">{device.displayName}</span>
            <span className="device-role">{device.role}</span>
          </div>
        </div>
        <span className={`device-status-badge ${statusClass}`}>
          {isOnline ? "Online" : "Offline"}
        </span>
      </div>

      <div className="device-card-metrics">
        {device.cpu !== undefined && (
          <div className="metric">
            <span className="metric-label">CPU</span>
            <span className="metric-value">{device.cpu}%</span>
          </div>
        )}
        {device.mem !== undefined && (
          <div className="metric">
            <span className="metric-label">Memory</span>
            <span className="metric-value">{device.mem}%</span>
          </div>
        )}
        {device.temp !== undefined && (
          <div className="metric">
            <span className="metric-label">Temp</span>
            <span className="metric-value">{device.temp}°C</span>
          </div>
        )}
        {device.disk !== undefined && (
          <div className="metric">
            <span className="metric-label">Disk</span>
            <span className="metric-value">{device.disk}%</span>
          </div>
        )}
        <div className="metric">
          <span className="metric-label">Last Seen</span>
          <span className="metric-value">
            {formatRelativeTime(device.lastSeen)}
          </span>
        </div>
        {uptimePercent !== null && (
          <div className="metric">
            <span className="metric-label">Uptime (24h)</span>
            <div className="uptime-container">
              <div className="uptime-bar">
                <div
                  className="uptime-fill"
                  style={{ width: `${uptimePercent}%` }}
                />
              </div>
              <span className="uptime-text">{uptimePercent}%</span>
            </div>
          </div>
        )}
      </div>

      {extraFields.length > 0 && (
        <div className="device-card-details">
          <div className="device-card-details-grid">
            {extraFields.map(({ label, value }) => (
              <div className="metric" key={label}>
                <span className="metric-label">{label}</span>
                <span className="metric-value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {device.consecutiveFailures > 0 && (
        <div className="device-alert-bar">
          <span className="alert-icon">⚠️</span>
          <span className="alert-text">
            {device.consecutiveFailures} consecutive failure{device.consecutiveFailures > 1 ? "s" : ""}
          </span>
          {device.error && <span className="alert-error">{device.error}</span>}
        </div>
      )}
    </div>
  );
}

function TimelineBar({ device, history }) {
  // Build 24h segments (48 x 30-minute slots)
  const now = Date.now();
  const dayAgo = now - 24 * 3600 * 1000;
  const slotDuration = 30 * 60 * 1000; // 30 minutes
  const slots = [];

  if (history.length === 0) {
    // No transitions — fill with current device status
    const fillStatus = device.status === "online" ? "online" : "unknown";
    for (let i = 0; i < 48; i++) {
      const slotStart = dayAgo + i * slotDuration;
      slots.push({
        status: fillStatus,
        time: new Date(slotStart).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      });
    }
  } else {
    // Determine the status before the first transition
    const firstTransitionStatus = history[0].status;
    const statusBeforeFirst = firstTransitionStatus === "offline" ? "online" : "offline";

    for (let i = 0; i < 48; i++) {
      const slotStart = dayAgo + i * slotDuration;
      const slotEnd = slotStart + slotDuration;

      // Find the most recent history entry at or before this slot's end
      const relevant = history.filter(
        (h) => new Date(h.timestamp + "Z").getTime() <= slotEnd
      );
      let status;
      if (relevant.length > 0) {
        status = relevant[relevant.length - 1].status;
      } else {
        // Before any transitions — use the inferred prior status
        status = statusBeforeFirst;
      }
      slots.push({
        status,
        time: new Date(slotStart).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      });
    }
  }

  return (
    <div className="timeline-row">
      <span className="timeline-device-name">{device.displayName}</span>
      <div className="timeline-bar">
        {slots.map((slot, i) => (
          <div
            key={i}
            className={`timeline-slot ${slot.status}`}
            title={`${slot.time} — ${slot.status}`}
          />
        ))}
      </div>
      <div className="timeline-labels">
        <span>24h ago</span>
        <span>Now</span>
      </div>
    </div>
  );
}

export default Hardware;
